import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "@/lib/http";

/**
 * Loads the full dealer list + filter metadata once. On subsequent visits
 * to the Dealers page the cached state is reused — no additional API calls.
 *
 * Idempotency is enforced by the caller (useEffect only dispatches when
 * status === "idle"). We deliberately do NOT guard inside the thunk body
 * because createAsyncThunk fires `.pending` synchronously — that would flip
 * status to "loading" before this function starts and cause the very first
 * fetch to short-circuit and never run.
 */
export const loadDealers = createAsyncThunk(
  "dealers/load",
  async (_arg, { rejectWithValue }) => {
    try {
      const [d, f] = await Promise.all([
        api.get("/dealers"),
        api.get("/dealers/filters"),
      ]);
      return {
        dealers: d.data || [],
        filters: f.data || { states: [], cities: [], cities_by_state: {} },
      };
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to load dealers");
    }
  }
);

const initialState = {
  dealers: [],
  filters: { states: [], cities: [], cities_by_state: {} },
  status: "idle", // idle | loading | succeeded | failed
  error: null,
};

const dealersSlice = createSlice({
  name: "dealers",
  initialState,
  reducers: {
    invalidateDealers() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDealers.pending, (state) => {
        if (state.status !== "succeeded") state.status = "loading";
        state.error = null;
      })
      .addCase(loadDealers.fulfilled, (state, action) => {
        if (action.payload) {
          state.dealers = action.payload.dealers;
          state.filters = action.payload.filters;
        }
        state.status = "succeeded";
      })
      .addCase(loadDealers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "Unknown error";
      });
  },
});

export const { invalidateDealers } = dealersSlice.actions;
export default dealersSlice.reducer;
